/**
 * FOS Semantic Search Component
 *
 * Prominent search input with:
 * - Visual emphasis as primary action
 * - Debounced input handling (managed by parent)
 * - Loading indicator with pulse animation
 * - Clear button
 * - Semantic search badge indicator
 */

import React, { useRef, useEffect } from 'react';
import { Textarea } from '../../ui/textarea';
import IconWrapper from '../../IconWrapper';
import { FiSearch, FiX, FiLoader } from 'react-icons/fi';
import { cn } from '../../../lib/utils';

interface FOSSemanticSearchProps {
  value: string;
  onChange: (value: string) => void;
  isSearching?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
}

const FOSSemanticSearch: React.FC<FOSSemanticSearchProps> = ({
  value,
  onChange,
  isSearching = false,
  placeholder = 'Search FOS decisions...',
  autoFocus = false,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (autoFocus && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [autoFocus]);

  const handleClear = () => {
    onChange('');
    textareaRef.current?.focus();
  };

  // Handle Enter key to prevent newlines (search on Enter)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card shadow-[var(--shadow-card)]">
      <div className="relative">
        <div className={cn(
          "absolute left-4 top-4 transition-colors duration-200",
          value ? "text-primary" : "text-muted-foreground"
        )}>
          {isSearching ? (
            <IconWrapper
              icon={FiLoader}
              className="h-5 w-5 animate-spin"
            />
          ) : (
            <IconWrapper icon={FiSearch} className="h-5 w-5" />
          )}
        </div>

        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          rows={4}
          className={cn(
            "min-h-[108px] resize-none border-0 bg-transparent pl-12 pr-14 py-3.5 text-[15px] shadow-none",
            "placeholder:text-muted-foreground/65",
            "focus-visible:ring-0 focus-visible:ring-offset-0"
          )}
        />

        <div className="absolute right-3 top-3.5 flex items-center gap-2">
          {value && (
            <button
              onClick={handleClear}
              className={cn(
                "rounded-md p-1.5 text-muted-foreground transition-colors duration-150",
                "hover:bg-muted hover:text-foreground"
              )}
              aria-label="Clear search"
            >
              <IconWrapper icon={FiX} className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FOSSemanticSearch;
